import { KeyboardArrowRight, PlayArrow } from "@mui/icons-material";
import { Box, Button, Grid, Typography } from "@mui/material";
import PageSection from "components/shared/PageSection";
// assets
import OrganoidPosterImage from "assets/images/presentations/2023_AACR_Organoid_poster_final.jpg";
import OrganoidPosterPDF from "assets/images/presentations/2023_AACR_Organoid_poster_final.pdf";
import ProteomicPosterImage from "assets/images/presentations/2023_AACR_Proteomic_Poster.jpg";
import ProteomicPosterPDF from "assets/images/presentations/2023_AACR_Proteomic_Poster.pdf";

/**
 * * Media section of the Homepage
 * @returns {React.ReactElement} Media
 */
export default function Media() {
  return (
    <PageSection
      id="media-section"
      backgroundSx={{
        backgroundColor: (theme) => theme.palette.secondary.main,
      }}
    >
      <Typography variant="h2">In the media</Typography>
      <OffsetGridContainer>
        <PressArticleCard href="https://www.targetedonc.com/view/ai-driven-blood-sample-analysis-helps-transform-breast-cancer-care">
          AI-Driven Blood Sample Analysis Helps Transform Breast Cancer Care
        </PressArticleCard>
        <PressArticleCard href="https://finance.yahoo.com/news/astrin-biosciences-leading-york-cancer-230400416.html">
          Astrin Biosciences and Leading New York Cancer Center Announce New Cancer
          Research Collaboration
        </PressArticleCard>
        <PressArticleCard href="https://www.allinahealth.org/about-us/news-releases/2023/allina-health-cancer-institute-clinical-trial-to-revolutionize-cancer-treatment-and-diagnosis">
          Allina Health Cancer Institute begins first-of-its-kind clinical trial to
          revolutionize cancer treatment and diagnosis
        </PressArticleCard>
      </OffsetGridContainer>
      <Box display="flex" maxWidth="100%" my={2} sx={{ aspectRatio: 16 / 9 }}>
        <iframe
          width="100%"
          height="100%"
          id="news-video"
          src="https://www.youtube.com/embed/HRr_qSjelx0"
          title="News Video"
          loading="lazy"
        />
      </Box>

      <Typography variant="h2" py={3}>
        Scientific publications
      </Typography>
      <OffsetGridContainer justify="left">
        <MediaOverlineItem>2024 JOURNAL OF ENDOCRINOLOGY</MediaOverlineItem>
        <PressArticleCard href="https://pubmed.ncbi.nlm.nih.gov/38366552/">
          Dissemination of Circulating Tumor Cells in Breast and Prostate Cancer: Implications for Early Detection
        </PressArticleCard>
        <Box sx={{height:'1.5em'}}>&nbsp;</Box>
        <MediaOverlineItem>2023 AACR ANNUAL MEETING</MediaOverlineItem>
        <PresentationCard imageHref={ProteomicPosterImage} pdfHref={ProteomicPosterPDF}>
          Unlocking the proteome of metastatic prostate cancer circulating tumor cells
        </PresentationCard>
        <PresentationCard imageHref={OrganoidPosterImage} pdfHref={OrganoidPosterPDF}>
          CTC-derived organoids from liver and pancreatic cancer patients for personalized
          therapy
        </PresentationCard>
      </OffsetGridContainer>
    </PageSection>
  );
}

/**
 * * Grid container with offset
 * @param {Object} props
 * @param {('left'|'right')} [props.justify="left"]
 * @param {number} [props.cols=12]
 * @param {number} [props.colOffset=3]
 * @param {React.ReactElement} props.children
 * @returns {React.ReactElement} OffsetGridContainer
 */
function OffsetGridContainer({
  justify = "left",
  cols = 12,
  colOffset = 3,
  children,
}) {
  if (cols < 0) {
    throw new Error("Invalid cols");
  }
  if (colOffset < 0 || colOffset > cols) {
    throw new Error("Invalid colOffset");
  }

  return (
    <Grid container data-testid="offset-grid-container">
      {justify === "right" && <Grid item xs={cols} md={colOffset} />}
      <Grid item xs={cols} md={cols - colOffset}>
        <Grid container>{children}</Grid>
      </Grid>
      {justify === "left" && <Grid item xs={cols} md={colOffset} />}
    </Grid>
  );
}

/**
 * * Overline item for media card stack
 * @param {Object} props
 * @param {React.ReactElement} props.children
 * @returns {React.ReactElement} MediaOverlineItem
 */
function MediaOverlineItem({ children }) {
  return (
    <Grid item container data-testid="media-section-overline-item">
      <Grid item xs={1} />
      <Grid item xs={11}>
        <Typography variant="overline" fontWeight="bold" color="common.white">
          {children}
        </Typography>
      </Grid>
    </Grid>
  );
}

/**
 * * Press article card with link and description
 * @param {Object} props
 * @param {string} props.href
 * @param {string} props.children
 * @returns {React.ReactElement} PressArticleCard
 */
function PressArticleCard({ href, children, target = "_blank" }) {
  return (
    <Grid item container data-testid="media-section-press-article-card">
      <Grid item xs={1} align="right">
        <KeyboardArrowRight color="primary" />
      </Grid>
      <Grid
        item
        xs={11}
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <Box
          height={10}
          width="100%"
          backgroundColor={(theme) => theme.palette.primary.main}
        />
      </Grid>
      <Grid item xs={1} />
      <Grid item xs={11}>
        <Typography variant="body2">{children}</Typography>
        <Button
          startIcon={<PlayArrow />}
          color="primary"
          sx={{ my: 1, p: 0.5 }}
          onClick={() => window.open(href, target)}
        >
          <b>View</b>
        </Button>
      </Grid>
    </Grid>
  );
}

/**
 * * Scientific presentation card with jpeg & pdf links and description
 * @param {Object} props
 * @param {string} props.imageHref
 * @param {string} props.pdfHref
 * @param {string} props.children
 * @returns {React.ReactElement} PresentationCard
 */
function PresentationCard({ imageHref, pdfHref, children }) {
  return (
    <Grid item container data-test-id="media-section-presentation-card">
      <Grid item xs={1} align="right">
        <KeyboardArrowRight color="primary" />
      </Grid>
      <Grid
        item
        xs={11}
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <Box
          height={10}
          width="100%"
          backgroundColor={(theme) => theme.palette.primary.main}
        />
      </Grid>
      <Grid item xs={1} />
      <Grid item xs={11}>
        <Typography variant="body2">{children}</Typography>
        <Button
          startIcon={<PlayArrow />}
          color="primary"
          sx={{ my: 1, p: 0.5 }}
          onClick={() => window.open(imageHref, "_blank")}
        >
          <b>Open</b>
        </Button>
        <Button
          startIcon={<PlayArrow />}
          color="primary"
          sx={{ ml: 5, my: 1, p: 0.5 }}
          onClick={() => window.open(pdfHref, "_blank")}
        >
          <b>PDF</b>
        </Button>
      </Grid>
      {/* Bottom padding */}
      <Grid item xs={12} height={20} />
    </Grid>
  );
}
